/**
 * CR-6191: OTIF Review sub-tab (Supplier Performance).
 *
 * Lists the "review queue" — delivered PO lines that failed OTIF, plus any that
 * already carry a decision (overridden/acknowledged) — and lets an admin
 * Override / Acknowledge / Reset each one. A decision filter narrows the view,
 * and an admin "Send test email" button fires the daily OTIF Review email on
 * demand (avoids the procurement-digest zero-opt-in trap). Ports SP CR-5987.
 *
 * CR-6519: the daily procurement digest deep-links here scoped to a single
 * counterparty (`&supplier=`) and decision state (`&otif_decision=`), so the
 * page opens on exactly the set the email counted. Both come from the URL, so
 * a refresh or a shared link resolves to the same view.
 *
 * CR-6521 rework: the queue is paged by the server and the decision filter is
 * applied by the server (CR-6315's `otif_decision`). It used to pull the whole
 * queue into the browser a page at a time and filter what it got, which
 * silently truncates and mis-filters on a real tenant — the review population
 * is every delivered line that ever failed OTIF, so it only grows.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Mail, RefreshCw, ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import PoLineLink from '../common/PoLineLink';
import PoHeaderLink from '../common/PoHeaderLink';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Pagination } from '../ui/pagination';
import { purchasingAPI, PurchaseOrderLine } from '../../lib/api';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { useFormatters } from '../../contexts/PreferencesContext';
import OTIFReviewActions from './OTIFReviewActions';
import OTIFBulkOverrideModal from './OTIFBulkOverrideModal';
import OTIFBulkResultDialog, { BulkResultEntry } from './OTIFBulkResultDialog';

// The API clamps page_size to 100 (StandardResultsSetPagination.max_page_size),
// so this stays under it. It also bounds a bulk batch: a run can only ever be
// as large as one page of eligible lines.
const PAGE_SIZE = 50;

type DecisionFilter = 'all' | 'not_decided' | 'overridden' | 'acknowledged';

const DECISION_FILTERS: readonly DecisionFilter[] = [
  'all', 'not_decided', 'overridden', 'acknowledged',
] as const;

/** Whether a URL-supplied value is one of the decision filters (CR-6519). */
function isDecisionFilter(value: string | null): value is DecisionFilter {
  return value !== null && (DECISION_FILTERS as readonly string[]).includes(value);
}

function decisionOf(line: PurchaseOrderLine): Exclude<DecisionFilter, 'all'> {
  if (line.otif_overridden) return 'overridden';
  if (line.otif_acknowledged) return 'acknowledged';
  return 'not_decided';
}

function DecisionBadge({ line }: { line: PurchaseOrderLine }) {
  const d = decisionOf(line);
  if (d === 'overridden') return <Badge className="bg-emerald-100 text-emerald-800">Overridden</Badge>;
  if (d === 'acknowledged') return <Badge className="bg-blue-100 text-blue-800">Acknowledged</Badge>;
  return <Badge variant="destructive">Not Decided</Badge>;
}

export default function OTIFReviewTab() {
  // CR-6307: render dates through the preference-aware central formatter so
  // AC-US (and every) user sees their configured date format with no day-shift.
  const { formatDate } = useFormatters();
  const [lines, setLines] = useState<PurchaseOrderLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // CR-6519: counterparty scope lives in the URL, not component state - the
  // email's count is per (customer, supplier), so the page must open on the
  // same one supplier or the number the recipient clicked will not match what
  // they land on. Non-numeric input is ignored rather than forwarded.
  const supplierParam = searchParams.get('supplier');
  const supplierFilter = supplierParam && /^\d+$/.test(supplierParam) ? supplierParam : null;
  const [decision, setDecision] = useState<DecisionFilter>(
    () => (isDecisionFilter(searchParams.get('otif_decision'))
      ? (searchParams.get('otif_decision') as DecisionFilter)
      : 'all'),
  );
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sendingTest, setSendingTest] = useState(false);
  // CR-6521: bulk OTIF override — multi-select the eligible (can_override_otif)
  // lines and override them in one action with a shared reason/comment.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  // The lines a non-clean run left behind for retry. The typed reason is kept
  // only while the selection is still those lines and nothing else — DERIVED,
  // never stored as a flag: `comments` is written to each line's changelog and
  // interpolated into the supplier's notification, so a reason typed for one
  // batch reaching another is an audit-trail and customer-facing defect, and a
  // flag would have to be cleared correctly on every path that empties the
  // selection (filter change, page change, deselect-all, the 404 recovery, the
  // post-refetch prune). Deriving it makes those paths impossible to miss.
  const [retryableIds, setRetryableIds] = useState<Set<number>>(new Set());
  const [bulkResultLog, setBulkResultLog] = useState<BulkResultEntry[]>([]);
  const [showBulkResultLog, setShowBulkResultLog] = useState(false);
  // Read between lines so a run can be stopped without aborting one in flight.
  const stopRequested = useRef(false);

  const fetchLines = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await purchasingAPI.getLines({
        review_candidates: true,
        // CR-6315 added this filter server-side precisely because filtering a
        // page in the browser lies as soon as the set spans more than one.
        otif_decision: decision,
        // Server-side (`po__supplier_id`), and ignored for supplier users -
        // filtering a page in the browser would misreport the total.
        ...(supplierFilter ? { supplier: supplierFilter } : {}),
        page,
        page_size: PAGE_SIZE,
      });
      // A NEW array every time, deliberately: the prune effect below is keyed
      // on `lines` and must fire on every response, so this does not depend on
      // the caller handing us fresh references.
      setLines([...(res.data.results || [])]);
      setTotalCount(res.data.count || 0);
    } catch (err) {
      // Under the Not Decided filter this queue SHRINKS as decisions are
      // recorded (under All it does not — an overridden line still matches),
      // so overriding the tail of the last page can leave us pointing past the
      // end. Another user's activity can do it under any filter. DRF answers an
      // out-of-range page with 404 "Invalid page." — rendering that as an error
      // would strand the user, because the only control on the error card
      // refetches the same dead page. Fall back to the first page instead; the
      // [decision, page] effect refetches.
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404 && page > 1) {
        setPage(1);
        return;
      }
      setError(getApiErrorMessage(err, 'Failed to load OTIF review lines'));
    } finally {
      setLoading(false);
    }
  }, [decision, page, supplierFilter]);

  useEffect(() => { fetchLines(); }, [fetchLines]);

  // Drop any selected id that is no longer an eligible line after a refetch.
  useEffect(() => {
    const eligible = new Set(lines.filter((l) => l.can_override_otif).map((l) => l.id));
    setSelectedIds((prev) => {
      const next = new Set(Array.from(prev).filter((id) => eligible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [lines]);

  const sendTest = async () => {
    try {
      setSendingTest(true);
      const res = await purchasingAPI.sendOtifReviewTest();
      toast.success(res.data.message || 'Test email sent');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to send test email'));
    } finally {
      setSendingTest(false);
    }
  };

  // CR-6521: only fully-delivered, not-yet-decided lines (admin-gated) are
  // eligible for override — ineligible rows have no checkbox and can't be
  // included in the batch.
  const selectableIds = lines.filter((l) => l.can_override_otif).map((l) => l.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
  const hasSelection = selectedIds.size > 0;
  // CR-6521 rework: bulk mode deadens each row's own Override / Acknowledge /
  // Reset, so it starts at TWO — the PO-Lines page this ticket names as the
  // format to copy does the same (POLinesTable.tsx: `size >= 2`). At one
  // selection the bar is still offered, as it is there (BulkActionBar.tsx).
  const bulkMode = selectedIds.size >= 2;
  // CR-6521 review: reflect a partial selection as indeterminate on the
  // select-all checkbox (Radix supports the tri-state), so users and
  // assistive tech get accurate feedback.
  const selectAllState: boolean | 'indeterminate' =
    allSelected ? true : hasSelection ? 'indeterminate' : false;

  const clearSelection = () => setSelectedIds(new Set());
  // Keep the typed reason only while the selection is exactly what the failed
  // run left behind (or a subset the user narrowed for the retry). Any other
  // selection is a different batch and gets a clean form.
  const preserveDraft = selectedIds.size > 0
    && Array.from(selectedIds).every((id) => retryableIds.has(id));

  const changeDecision = (v: DecisionFilter) => {
    setDecision(v); setPage(1); clearSelection();
    // Keep the URL honest so it stays shareable and survives a refresh - an
    // `otif_decision` left over from the email link would otherwise contradict
    // the dropdown the user just changed.
    const next = new URLSearchParams(searchParams);
    if (v === 'all') next.delete('otif_decision'); else next.set('otif_decision', v);
    setSearchParams(next, { replace: true });
  };

  // CR-6519: widen from the emailed single-counterparty view to the whole
  // queue. `replace` so the digest link does not become a back-button trap.
  const clearSupplierFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('supplier');
    setSearchParams(next, { replace: true });
    setPage(1); clearSelection();
  };

  // Named from the rows themselves; the id is all the URL carries, and a
  // filter matching nothing still has to say what it is filtering to.
  const supplierLabel = lines[0]?.supplier_name || `Supplier #${supplierFilter}`;
  // A batch never spans pages: what you can see is what you can submit.
  const changePage = (p: number) => { setPage(p); clearSelection(); };
  const toggleOne = (id: number) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => (allSelected ? clearSelection() : setSelectedIds(new Set(selectableIds)));

  const submitBulkOverride = async (comments: string) => {
    const ids = Array.from(selectedIds);
    const lineMap = new Map(lines.map((l) => [l.id, l]));
    stopRequested.current = false;
    setBulkSaving(true);
    setBulkProgress({ done: 0, total: ids.length });

    let ok = 0;
    const failed: BulkResultEntry[] = [];
    const unprocessed: number[] = [];
    for (const id of ids) {
      if (stopRequested.current) { unprocessed.push(id); continue; }
      try {
        await purchasingAPI.overrideOtif(id, { comments });
        ok += 1;
      } catch (err) {
        const info = lineMap.get(id);
        failed.push({
          lineId: id,
          poNumber: info?.po_number || String(id),
          lineNumber: info?.line_number || 0,
          error: getApiErrorMessage(err, 'Unknown error'),
        });
      }
      setBulkProgress((p) => (p ? { ...p, done: p.done + 1 } : p));
    }

    setBulkSaving(false);
    setBulkProgress(null);
    setBulkOpen(false);

    if (ok > 0) {
      toast.success(`Overrode ${ok} line${ok !== 1 ? 's' : ''} successfully`);
    }
    if (failed.length > 0) {
      toast.error(`Failed to override ${failed.length} line${failed.length !== 1 ? 's' : ''} — see details below`);
      setBulkResultLog(failed);
      setShowBulkResultLog(true);
    }
    if (unprocessed.length > 0) {
      toast.warning(`Stopped — ${unprocessed.length} line${unprocessed.length !== 1 ? 's' : ''} not processed`);
    }
    // Anything that did not go through stays selected so it can be retried;
    // only a clean run clears the selection.
    const retryable = new Set<number>([...failed.map((f) => f.lineId), ...unprocessed]);
    setRetryableIds(retryable);
    if (retryable.size > 0) setSelectedIds(retryable); else clearSelection();
    fetchLines();
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>OTIF Review</CardTitle>
          <CardDescription>
            Delivered lines that failed OTIF — override, acknowledge, or reset each decision.
            {supplierFilter ? ' Scoped to one supplier from your daily summary.' : ''}
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {supplierFilter && (
            <Badge variant="secondary" className="gap-1 py-1 pl-2.5 pr-1">
              {supplierLabel}
              <button
                type="button"
                onClick={clearSupplierFilter}
                aria-label="Show all suppliers"
                title="Show all suppliers"
                className="rounded-sm p-0.5 hover:bg-muted-foreground/20"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </Badge>
          )}
          <Select value={decision} onValueChange={(v) => changeDecision(v as DecisionFilter)}>
            <SelectTrigger className="w-[170px]">
              <SelectValue placeholder="Decision" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All decisions</SelectItem>
              <SelectItem value="not_decided">Not Decided</SelectItem>
              <SelectItem value="overridden">Overridden</SelectItem>
              <SelectItem value="acknowledged">Acknowledged</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchLines} disabled={loading} className="gap-1.5">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={sendTest} disabled={sendingTest} className="gap-1.5">
            {sendingTest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Send test email
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />Loading…
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-700">{error}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={fetchLines}>Try again</Button>
          </div>
        ) : lines.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            No lines to review
            {supplierFilter ? ` for ${supplierLabel}` : ''}
            {decision !== 'all' ? ' for this filter' : ''}.
            {supplierFilter && (
              <Button variant="link" size="sm" onClick={clearSupplierFilter}>
                Show all suppliers
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* CR-6521: bulk-select action bar — mirrors the PO-Lines multi-select. */}
            {hasSelection && (
              <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm">
                <span className="font-medium">
                  {selectedIds.size} line{selectedIds.size !== 1 ? 's' : ''} selected
                </span>
                <span className="text-muted-foreground">
                  {bulkMode
                    ? '— bulk mode: per-line actions disabled. Use the button here, or clear the selection.'
                    : '— select another line for bulk mode, or use this line\'s own actions.'}
                </span>
                <Button size="sm" className="ml-auto gap-1.5" onClick={() => setBulkOpen(true)}>
                  <ShieldCheck className="h-4 w-4" />Override OTIF
                </Button>
                <Button variant="ghost" size="sm" onClick={clearSelection}>Clear</Button>
              </div>
            )}
            <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <Checkbox
                    checked={selectAllState}
                    onCheckedChange={toggleAll}
                    disabled={selectableIds.length === 0}
                    aria-label="Select all eligible lines"
                  />
                </TableHead>
                <TableHead>PO / Line</TableHead>
                <TableHead>Part</TableHead>
                <TableHead>Supplier</TableHead>
                <TableHead className="text-right">Received / Ordered</TableHead>
                <TableHead>Required date</TableHead>
                <TableHead className="text-right">GRN score</TableHead>
                <TableHead>Decision</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((line) => (
                <TableRow key={line.id} data-state={selectedIds.has(line.id) ? 'selected' : undefined}>
                  <TableCell>
                    {line.can_override_otif ? (
                      <Checkbox
                        checked={selectedIds.has(line.id)}
                        onCheckedChange={() => toggleOne(line.id)}
                        aria-label={`Select ${line.po_number || 'PO'} line ${line.line_number}`}
                      />
                    ) : null}
                  </TableCell>
                  <TableCell className="font-medium whitespace-nowrap">
                    {/* CR-1154: PO number links to the PO header; line to the line item. */}
                    <PoHeaderLink poNumber={line.po_number}>{line.po_number || line.po}</PoHeaderLink>
                    {' / '}
                    <PoLineLink poLineId={line.id}>{line.line_number}</PoLineLink>
                  </TableCell>
                  <TableCell>{line.part_number}</TableCell>
                  <TableCell>{line.supplier_name}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {line.received_qty} / {line.quantity}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{line.delivery_date ? formatDate(line.delivery_date) : '—'}</TableCell>
                  <TableCell className="text-right">
                    {line.grn_score != null ? `${line.grn_score}%` : '—'}
                  </TableCell>
                  <TableCell><DecisionBadge line={line} /></TableCell>
                  <TableCell className="text-right">
                    <OTIFReviewActions line={line} onDone={fetchLines} disabled={bulkMode} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </>
        )}
        {/* Outside the branches above on purpose, and deliberately NOT hidden by
            the error state: a failure on page 3 otherwise leaves an error card
            whose only control refetches page 3 — the same dead end the 404
            recovery exists to prevent, one status code over. No page-size
            selector: the shared control offers sizes above the API's 100-row
            clamp, which would silently return fewer rows than the page count is
            computed from. */}
        {!loading && (
          <Pagination
            currentPage={page}
            totalPages={Math.ceil(totalCount / PAGE_SIZE)}
            totalCount={totalCount}
            itemsPerPage={PAGE_SIZE}
            onPageChange={changePage}
            itemLabel="lines"
          />
        )}
      </CardContent>
      <OTIFBulkOverrideModal
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        count={selectedIds.size}
        saving={bulkSaving}
        progress={bulkProgress}
        preserveDraft={preserveDraft}
        onStop={() => { stopRequested.current = true; }}
        onSubmit={submitBulkOverride}
      />
      <OTIFBulkResultDialog
        open={showBulkResultLog}
        onOpenChange={setShowBulkResultLog}
        entries={bulkResultLog}
      />
    </Card>
  );
}
